import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
// action creators
import {
  setAllCarBrands,
  setAllCarModels,
  setAllCarYears,
  setCarBrand,
  setCarDetails,
  setCarModel,
  setCarYear,
  setSelectedCar,
} from "../../redux/car";

import Select from "../Select/Select";
import Ripple from "../ui/spinners/ripple/Ripple";
import Modal from "../ui/Modal/Modal";
import {
  getAllCarBrandsInInventoryForSalesWebsite,
  getAllCarModelsInInventoryGivenCarBrandId,
  getAllCarsInInventoryGivenCarModelId,
} from "../../services/car";
import { setExpandedIndex } from "../../redux/menuTracker";
import logo from "../../assets/images/logo.png";
import { isValidUrl } from "../../helpers/isValidUrl";

const CarDropdowns = (props) => {
  const {
    car: {
      brandName,
      modelName,
      startYear,
      endYear,
      transmission,
      cc,
      id: carId,
    } = {},
  } = props;

  // redux
  const {
    allCarBrands: brandsList,
    allCarModels: modelsList,
    allCarYears: yearsList,
    selectedCar,
  } = useSelector((state) => state.car);
  const { createdFromOrderId } = useSelector((state) => state.order);

  const dispatch = useDispatch();

  // state
  const [brandsLoading, setBrandsLoading] = useState(false);
  const [isModelModalOpen, setIsModelModalOpen] = useState(false);
  const [isBrandModalOpen, setIsBrandModalOpen] = useState(false);
  const [brandSearchTerm, setBrandSearchTerm] = useState("");
  const [modelSearchTerm, setModelSearchTerm] = useState("");

  const expandedIndexHanlder = (option) => {
    dispatch(setExpandedIndex(option));
  };

  // handlers
  const carBrandSelectedHandler = (option) => {
    setIsBrandModalOpen(false);
    dispatch(setCarBrand(option));
    dispatch(setCarModel());
    dispatch(setAllCarModels([]));
    expandedIndexHanlder({});
  };

  const carModelSelectedHandler = (option) => {
    setIsModelModalOpen(false);
    dispatch(setCarModel(option));
    dispatch(setCarYear());
    dispatch(setAllCarYears([]));
    expandedIndexHanlder({});
  };

  const carYearSelectedHandler = (option) => {
    dispatch(setCarYear(option));
    dispatch(setCarDetails());
    expandedIndexHanlder({});
  };

  const carDetailsSelectedHandler = (option) => {
    dispatch(setCarDetails(option));
    expandedIndexHanlder({});
  };

  useEffect(() => {
    if (carId) {
      dispatch(
        setSelectedCar({
          brand: { brandName },
          model: { modelName },
          year: { startYear, endYear },
          carDetails: { id: carId, cc, transmission },
        })
      );
    }
  }, [
    brandName,
    modelName,
    startYear,
    endYear,
    cc,
    transmission,
    carId,
    dispatch,
  ]);

  const getCarDetails = () => {
    if (selectedCar.year) {
      return { disabled: false, options: [selectedCar.year] };
    } else {
      return { disabled: true, options: [] };
    }
  };

  useEffect(() => {
    const getBrands = async () => {
      setBrandsLoading(true);
      try {
        const response = await getAllCarBrandsInInventoryForSalesWebsite();
        dispatch(setAllCarBrands(response.data.carBrandsList));
      } catch (error) {
        console.log(error);
      }
      setBrandsLoading(false);
    };

    if (brandsList.length === 0) {
      getBrands();
    }
  }, [brandsList, dispatch]);

  useEffect(() => {
    const getModels = async () => {
      try {
        const response = await getAllCarModelsInInventoryGivenCarBrandId(
          selectedCar.brand.id
        );
        dispatch(setAllCarModels(response.data.carModelList));
      } catch (error) {
        console.log(error);
      }
    };

    if (selectedCar.brand) {
      getModels();
    }
  }, [dispatch, selectedCar.brand]);

  useEffect(() => {
    const getYears = async () => {
      try {
        const response = await getAllCarsInInventoryGivenCarModelId(
          selectedCar.model.id
        );
        dispatch(setAllCarYears(response.data.carList));
      } catch (error) {
        console.log(error);
      }
    };

    if (selectedCar.model) {
      getYears();
    }
  }, [dispatch, selectedCar.model]);

  const getCarYearInitialValue = () => {
    if (selectedCar.year?.startYear) {
      return `${selectedCar.year.startYear}-${selectedCar.year.endYear}`;
    } else if (startYear) {
      return `${startYear}-${endYear}`;
    } else {
      return "";
    }
  };

  const getTransmissionInitialValue = () => {
    if (selectedCar.carDetails?.transmission) {
      return `${selectedCar.carDetails.transmission}-${selectedCar.carDetails.cc}`;
    } else if (transmission) {
      return `${transmission}-${cc}`;
    } else {
      return "";
    }
  };

  // Filter brands and models based on search terms
  const filteredBrandsList = brandsList.filter((brand) =>
    brand.brandName.toLowerCase().includes(brandSearchTerm.toLowerCase())
  );

  const filteredModelsList = modelsList.filter((model) =>
    model.modelName.toLowerCase().includes(modelSearchTerm.toLowerCase())
  );

  let content;
  if (brandsLoading) {
    content = (
      <div className="flex mb-8 justify-content-center">
        <Ripple className="border-dark-blue" />
      </div>
    );
  } else if (brandsList.length !== 0) {
    content = (
      <section
        dir="rtl"
        className="mx-4 lg:mx-auto max-w-4xl mb-8 grid items-center gap-6 grid-cols-1 sm:grid-cols-4"
      >
        {/* Button to open brand modal */}
        <button
          className="border p-2 rounded"
          onClick={() => setIsBrandModalOpen(true)}
          disabled={createdFromOrderId}
        >
          {selectedCar.brand?.brandName || brandName || "ماركة السيارة"}
        </button>

        {/* Modal for selecting car brand */}
        {isBrandModalOpen && (
          <Modal close={() => setIsBrandModalOpen(false)} classNames="top-8">
            <div>
              <h2 className="text-center mb-4">اختر ماركة السيارة</h2>
              <input
                type="text"
                placeholder="ابحث بالماركة"
                value={brandSearchTerm}
                onChange={(e) => setBrandSearchTerm(e.target.value)}
                className="block w-full border rounded mb-4 p-2"
              />
              <ul className="max-h-100 overflow-y-auto grid grid-cols-4 gap-4">
                {filteredBrandsList.map((brand, index) => (
                  <li
                    key={index}
                    className="cursor-pointer hover:bg-gray-100 flex flex-col items-center p-4"
                    onClick={() => carBrandSelectedHandler(brand)}
                  >
                    <img
                      src={isValidUrl(brand.imageUrl) ? brand.imageUrl : logo}
                      alt={brand.brandName}
                      className="w-full h-full object-center object-contain"
                    />
                    <span className="text-center">{brand.brandName}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Modal>
        )}

        {/* Button to open model modal */}
        <button
          className="border p-2 rounded"
          onClick={() => setIsModelModalOpen(true)}
          disabled={!selectedCar.brand || createdFromOrderId}
        >
          {selectedCar.model?.modelName || modelName || "موديل السيارة"}
        </button>

        {/* Modal for selecting car model */}
        {isModelModalOpen && (
          <Modal close={() => setIsModelModalOpen(false)} classNames="top-8">
            <div>
              <h2 className="text-center mb-4">اختر موديل السيارة</h2>
              <input
                type="text"
                placeholder="ابحث بالموديل"
                value={modelSearchTerm}
                onChange={(e) => setModelSearchTerm(e.target.value)}
                className="block w-full border rounded mb-4 p-2"
              />
              <ul className="max-h-100 overflow-y-auto grid grid-cols-4 gap-4">
                {filteredModelsList.map((model, index) => (
                  <li
                    key={index}
                    className="cursor-pointer hover:bg-gray-100 flex flex-col items-center p-4"
                    onClick={() => carModelSelectedHandler(model)}
                  >
                    <img
                      src={isValidUrl(model.imageUrl) ? model.imageUrl : logo}
                      alt={model.modelName}
                      className="w-full h-full object-center object-contain"
                    />
                    <span className="text-center">{model.modelName}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Modal>
        )}

        <Select
          placeholder="سنة السيارة"
          options={yearsList}
          optionLabelProp="year"
          onSelect={carYearSelectedHandler}
          isDisabled={!selectedCar.model || createdFromOrderId}
          initialValue={getCarYearInitialValue}
          isLoading={!yearsList?.length}
        />

        <Select
          placeholder="الفتيس"
          options={getCarDetails().options}
          optionLabelProp="transmission"
          onSelect={carDetailsSelectedHandler}
          isDisabled={getCarDetails().disabled || createdFromOrderId}
          initialValue={getTransmissionInitialValue}
        />
      </section>
    );
  }

  return content;
};

export default CarDropdowns;
